/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import { removeFromGroup, addToGroup } from "src/services/UserService";

/**
 * The ChangeGroupModal component
 *
 * @author Thais Watanabe
 */
const ChangeGroupModal = (props) => {
  const { setIsChangeOpen, isChangeOpen, listUsers, selectedUser } = props;
  const [username, setUsername] = useState("");
  const [group, setGroup] = useState("User");
  const [error, setError] = useState(null);

  const cancelButtonRef = useRef(null);

  /**
   * useEffect
   */
  useEffect(() => {
    setUsername(props.selectedUser ? props.selectedUser.Username : null);
    setGroup(props.selectedUser ? props.selectedUser.Group : "");

    return () => {
      setUsername("");
      setGroup("User");
    };
  }, [props]);

  /**
   * removeUserFromGroup
   */
  async function removeUserFromGroup() {
    setError(null);
    await removeFromGroup(username, selectedUser)
      .then(() => {
        addUserToGroup();
      })
      .catch((error) => {
        setError(error.response.data.message);
        console.log(error.response);
      });
  }

  /**
   * addUserToGroup
   */
  async function addUserToGroup() {
    await addToGroup(username, group)
      .then(() => {
        // reset form
        setUsername("");
        setGroup("User");
        listUsers();
        setIsChangeOpen(!isChangeOpen);
        toast.success("保存しました");
      })
      .catch((error) => {
        setError(error.response.data.message);
        toast.error("エラーが発生しました");
        console.log(error.response);
      });
  }

  return (
    <Transition.Root show={isChangeOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={setIsChangeOpen}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  onClick={() => setIsChangeOpen(!isChangeOpen)}
                >
                  <span className="sr-only">Close</span>
                  {/* <!-- Heroicon name: x --> */}
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <form>
                <div>
                  <div>
                    <h2 className="text-lg leading-6 font-medium text-gray-900">
                      グループ変更する
                    </h2>
                  </div>

                  {error && (
                    <div className="mt-6 rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          {/* <!-- Heroicon name: exclamation --> */}
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            エラーが発生しました
                          </h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="username"
                        className="block text-sm font-medium text-gray-700"
                      >
                        ユーザー名
                      </label>
                      <input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        type="text"
                        name="username"
                        disabled
                        id="username2"
                        autoComplete="cc-given-name"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="email_address"
                        className="block text-sm font-medium text-gray-700"
                      >
                        ユーザー・グループ
                      </label>
                      <select
                        id="result"
                        name="result"
                        autoComplete="result"
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                        value={group}
                        onChange={(e) => setGroup(e.currentTarget.value)}
                      >
                        <option>admin</option>
                        <option>user</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    onClick={() => removeUserFromGroup()}
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:col-start-2 sm:text-sm"
                  >
                    保存する
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => setIsChangeOpen(!isChangeOpen)}
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ChangeGroupModal;
